import api from "../../service/api";
import { useEffect, useMemo } from "react";
import { useStore } from "../../store";
import { useNavigate } from "react-router-dom";
import { Calendar, MapPin, CheckCircle2, Clock, ArrowUpRight } from "lucide-react";
import { formatDate, hasPermission } from "../../utils/helpers";
import { AdminMetrics } from "./AdminMetrics";

export function Content() {
  const events = useStore((store) => store.events);
  const setEvents = useStore((store) => store.setEvents);
  const user = useStore((store) => store.user);
  const navigate = useNavigate();

  const canViewStats = hasPermission(user.user_type);

  function allColetas() {
    api
      .get("events/all")
      .then((response) => {
        setEvents(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  useEffect(() => {
    allColetas();
  }, []);

  const stats = useMemo(() => {
    const totalEvents = events.length;
    const activeEvents = events.filter((event) => {
      const today = new Date();
      const startDate = new Date(event.initial_date);
      const endDate = new Date(event.finish_date);
      return today >= startDate && today <= endDate;
    }).length;
    const upcomingEvents = events.filter((event) => {
      const today = new Date();
      const startDate = new Date(event.initial_date);
      return today < startDate;
    }).length;
    const eventsWithLocation = events.filter(
      (event) => event.latitude && event.longitude
    ).length;

    return {
      totalEvents,
      activeEvents,
      upcomingEvents,
      eventsWithLocation,
    };
  }, [events]);

  const statCards = [
    {
      label: "Total de eventos",
      value: stats.totalEvents,
      icon: Calendar,
      valueClass: "text-apcap dark:text-emerald-300",
      iconBg: "bg-apcap-muted text-apcap dark:bg-apcap/20 dark:text-emerald-300",
    },
    {
      label: "Eventos ativos",
      value: stats.activeEvents,
      icon: CheckCircle2,
      valueClass: "text-emerald-600 dark:text-emerald-400",
      iconBg:
        "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
    },
    {
      label: "Próximos eventos",
      value: stats.upcomingEvents,
      icon: Clock,
      valueClass: "text-sky-600 dark:text-sky-400",
      iconBg: "bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300",
    },
    {
      label: "Com localização",
      value: stats.eventsWithLocation,
      icon: MapPin,
      valueClass: "text-violet-600 dark:text-violet-400",
      iconBg:
        "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
    },
  ];

  return (
    <>
      <main className="space-y-10 pb-4">
        {canViewStats && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="ds-card-interactive group p-5 dark:shadow-none"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                      {card.label}
                    </p>
                    <p
                      className={`mt-2 text-3xl font-semibold tabular-nums tracking-tight ${card.valueClass}`}
                    >
                      {card.value}
                    </p>
                  </div>
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${card.iconBg}`}
                  >
                    <card.icon className="h-5 w-5" aria-hidden />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {canViewStats && <AdminMetrics />}

        <section className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-900 dark:text-white">
              Eventos
            </h2>
            <button
              type="button"
              onClick={() => navigate("/eventos")}
              className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-apcap transition hover:text-apcap-light dark:text-emerald-400 dark:hover:text-emerald-300"
            >
              Ver todos
              <ArrowUpRight className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {events.length === 0 ? (
            <div className="ds-card flex flex-col items-center justify-center px-6 py-16 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-zinc-100 dark:bg-zinc-800">
                <Calendar className="h-7 w-7 text-zinc-400" aria-hidden />
              </div>
              <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                Nenhum evento cadastrado ainda.
              </p>
              <p className="mt-1 max-w-sm text-xs text-zinc-500 dark:text-zinc-500">
                Os eventos aparecerão aqui assim que forem criados.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {events.map((event) => {
                const isActive =
                  new Date() >= new Date(event.initial_date) &&
                  new Date() <= new Date(event.finish_date);
                const isUpcoming = new Date() < new Date(event.initial_date);

                return (
                  <article
                    key={event.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate("/eventos");
                      }
                    }}
                    className="group ds-card-interactive cursor-pointer overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-apcap focus-visible:ring-offset-2 dark:focus-visible:ring-offset-zinc-950"
                    onClick={() => navigate("/eventos")}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-zinc-100 dark:bg-zinc-800">
                      <img
                        className="h-full w-full object-cover transition duration-500 ease-out group-hover:scale-[1.03]"
                        src={event.banner_url}
                        alt=""
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/50 to-transparent opacity-60" />
                      <div className="absolute right-2 top-2 flex max-w-[90%] flex-wrap justify-end gap-1.5">
                        {isActive && (
                          <span className="rounded-full bg-emerald-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">
                            Ativo
                          </span>
                        )}
                        {isUpcoming && (
                          <span className="rounded-full bg-sky-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">
                            Em breve
                          </span>
                        )}
                        {event.enable_subscription && (
                          <span className="rounded-full bg-apcap px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm">
                            Inscrições abertas
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="space-y-3 p-4">
                      <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-zinc-900 dark:text-white">
                        {event.name}
                      </h3>
                      <div className="space-y-2 text-xs text-zinc-600 dark:text-zinc-400">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                          <span>
                            {formatDate(event.initial_date)} —{" "}
                            {formatDate(event.finish_date)}
                          </span>
                        </div>
                        {event.latitude && event.longitude && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                            <span>Localização definida</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </>
  );
}
